import { useLocation, useNavigate } from "react-router-dom"
import { useEffect, useState } from "react"
import { Loader, Dimmer } from "semantic-ui-react"
import getStorageUser from "../middlewares/GetStorageUser"
import { ObjNotificaciones } from "../config/notificaciones";
import { esTokenDeInvitado, porVencer, tokenVencido } from "./ValidateTokenExistence";
import { confirmarVigencia } from "./vigenciaDelToken";

const ignoredRoutes: string[] = [
    '/',
    '/login',
    '/usuario/registrarUsuario',
    '/usuario/registrarusuario',
    '/usuario/RegistroExitoso',
    '/usuario/registroexitoso',
    '/usuario/CambiarContrasena',
    '/usuario/cambiarcontrasena'
]

const ValidateAccessMiddleware = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const [loading, setLoading] = useState<any>(false)

    const cerrarSesion = (motivo: string) => {
        console.warn(`[sesión] se cerró la sesión: ${motivo}`);
        localStorage.clear();
        ObjNotificaciones.MSG_ERROR('warning', 'Sesión expirada', 'Tú sesión ha expirado, intenta iniciar sesión nuevamente');
        return navigate('/login');
    }

    /**
     * Comprueba la sesión en cada cambio de pantalla.
     *
     * Dos cosas gobiernan esto, y las dos vienen de haber sacado a gente con
     * su token intacto:
     *
     * 1. La vigencia se mira primero en el propio token, que lleva dentro su
     *    fecha. Antes se le preguntaba al servicio de usuarios en CADA
     *    navegación, y cada una de esas preguntas era una ocasión más de que
     *    un tropiezo pasajero cerrara la sesión.
     * 2. Solo se cierra cuando consta que el token no sirve. Si no se pudo
     *    comprobar —se cayó la red, el servicio respondió que no pudo— se
     *    sigue con el que hay: si de verdad está muerto, la primera petición
     *    real lo dirá.
     */
    const validateAccess = async () => {
        const token = localStorage.getItem('token')
        const user = getStorageUser()

        if (!token) {
            return cerrarSesion("no hay token guardado");
        }

        if (!user) {
            /*
              Un token de invitado no es una sesion, asi que no hay nada que
              haya caducado. Queda escrito al cerrar sesion, cuando alguna
              peticion pendiente pide uno, y avisar de "sesion expirada" por
              verlo repetia el aviso en cada pantalla.
            */
            if (esTokenDeInvitado(token)) {
                console.warn("[sesión] token de invitado en una ruta con sesión: al login, sin aviso");
                return navigate("/login");
            }

            return cerrarSesion("hay token pero no hay usuario guardado");
        }

        if (tokenVencido(token)) {
            return cerrarSesion("el token traía dentro una fecha ya vencida");
        }

        if (porVencer(token)) {
            setLoading(true)
            try {
                // Compartida con el interceptor: una pregunta por rafaga.
                const data = await confirmarVigencia(token);

                // 400 es "el token no sirve". Cualquier otra cosa —503 cuando
                // no se pudo comprobar, o una forma que no se reconoce— no
                // alcanza para cerrarle la sesión a nadie.
                if (data?.status === 400) {
                    cerrarSesion(`users/token respondió 400: ${JSON.stringify(data?.message ?? "")}`);
                    setTimeout(() => {
                        window.location.reload();
                    }, 2000);
                    return;
                }
            } catch (error) {
                console.warn('No se pudo confirmar la sesión; se continúa con el token actual.', error);
            } finally {
                setLoading(false)
            }
        }

        if (location?.pathname?.indexOf('/administrador') !== -1) {
            if (location?.pathname !== '/administrador' && user?.role_id !== 1) {
                if (!user?.permissions?.some(permiso => permiso?.permission?.route[0]?.route === location?.pathname)) {
                    // Redirigir al usuario a la página de administrador
                    return navigate('/administrador');
                }
            }
        }
    }

    const checkRoutes = () => {
        const currentPath = (location?.pathname || '').replace(/\/$/, '').toLowerCase();
        const isIgnored = ignoredRoutes.some(
            (route) => route.replace(/\/$/, '').toLowerCase() === currentPath
        );
        return !isIgnored;
    }

    useEffect(() => {
        if (checkRoutes()) {
            validateAccess()
        }
    }, [location])

    return (
        <>
            {
                loading && (
                    <Dimmer active={loading} inverted>
                        <Loader size="massive" inverted />
                    </Dimmer>
                )
            }
        </>
    )
}

export default ValidateAccessMiddleware